import { useQuery } from '@tanstack/react-query';
import { employeesService } from '../services/employees.service';
import { employeeHierarchyService } from '../services/employee-hierarchy.service';
import type {
  GetAllEmployeesApiRequest,
  GetManagerEmployeesApiRequest,
} from '@momentum/types';

// Query keys for employees
export const employeeKeys = {
  all: ['employees'] as const,
  lists: () => [...employeeKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) =>
    [...employeeKeys.lists(), { filters }] as const,
  details: () => [...employeeKeys.all, 'detail'] as const,
  detail: (id: string, companyId: string) =>
    [...employeeKeys.details(), id, companyId] as const,
  manager: (id: string, companyId: string) =>
    [...employeeKeys.detail(id, companyId), 'manager'] as const,
  relations: (id: string, companyId: string) =>
    [...employeeKeys.detail(id, companyId), 'relations'] as const,
  managerEmployees: (params: GetManagerEmployeesApiRequest) =>
    [...employeeKeys.all, 'managerEmployees', params] as const,
  hierarchy: (companyId: string) =>
    [...employeeKeys.all, 'hierarchy', companyId] as const,
};

// Get all employees by company ID
export const useEmployees = (
  params: GetAllEmployeesApiRequest,
  enabled = true
) => {
  return useQuery({
    queryKey: employeeKeys.list(params),
    queryFn: () => employeesService.getAllEmployees(params),
    enabled: enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Get single employee by ID
export const useEmployee = (id?: string, companyId?: string) => {
  console.log('useEmployee', id, companyId);
  return useQuery({
    queryKey: employeeKeys.detail(id as string, companyId as string),
    queryFn: () =>
      employeesService.getEmployeeById(id as string, companyId as string),
    enabled: !!id && !!companyId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Get employee manager
export const useEmployeeManager = (id: string, companyId: string) => {
  return useQuery({
    queryKey: employeeKeys.manager(id, companyId),
    queryFn: () => employeesService.getEmployeeManager(id, companyId),
    enabled: !!id && !!companyId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Get employee relations
export const useEmployeeRelations = (id: string, companyId: string) => {
  return useQuery({
    queryKey: employeeKeys.relations(id, companyId),
    queryFn: () => employeesService.getEmployeeRelations(id, companyId),
    enabled: !!id && !!companyId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Get manager employees (all levels)
export const useManagerEmployees = (params: GetManagerEmployeesApiRequest) => {
  return useQuery({
    queryKey: employeeKeys.managerEmployees(params),
    queryFn: () => employeesService.getManagerEmployees(params),
    enabled: !!params.managerId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Get manager direct employees only (one level)
export const useManagerDirectEmployees = (
  params: GetManagerEmployeesApiRequest
) => {
  return useQuery({
    queryKey: [...employeeKeys.all, 'managerDirectEmployees', params] as const,
    queryFn: () => employeesService.getManagerDirectEmployees(params),
    enabled: !!params.managerId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Get eligible coworkers (excludes manager and direct reports)
export const useEligibleCoworkers = (enabled = true) => {
  return useQuery({
    queryKey: [...employeeKeys.all, 'eligibleCoworkers'] as const,
    queryFn: () => employeesService.getEligibleCoworkers(),
    enabled: enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Get company employee hierarchy (tree) – system admin only for other companies
export const useCompanyEmployeeHierarchy = (companyId: string | null) => {
  return useQuery({
    queryKey: employeeKeys.hierarchy(companyId ?? ''),
    queryFn: () => employeeHierarchyService.getCompanyHierarchy(companyId!),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
